import { forwardRef } from 'react';
import { Chip, ChipProps, Theme } from '@mui/material';
import { mixinSx } from '@watershed/style/styleUtils';

export type CalloutChipProps = Pick<ChipProps, 'size' | 'icon' | 'sx'> & {
  // Add custom props
  label?: React.ReactNode;
  variant?: 'filled' | 'outlined';
  color?:
    | {
        text: string;
        background: string;
        border: string;
        borderDashed?: string;
      }
    | keyof Theme['palette'];
  offset?: number;
};

const CalloutChip = forwardRef(
  (
    {
      size,
      icon,
      sx,
      label,
      color = {
        text: 'cobalt50',
        background: 'cobalt05',
        border: 'cobalt20',
        borderDashed: 'cobalt20',
      },
      offset = -2,
    }: CalloutChipProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const getColors = () => {
      if (typeof color === 'string') {
        // If using theme color - e.g. primary, error, success, etc.
        return {
          color: `${color}.main`,
          backgroundColor: `${color}.light`,
          borderColor: `${color}.main`,
          dashedBorderColor: `${color}.main`,
        };
      }
      // If using palette color - e.g. grey, cobalt, etc.
      return {
        color: color.text,
        backgroundColor: color.background,
        borderColor: color.border,
        dashedBorderColor: color.borderDashed || color.border,
      };
    };

    const colors = getColors();

    return (
      <Chip
        label={label}
        size={size}
        icon={icon}
        ref={ref}
        sx={mixinSx(
          {
            position: 'relative',
            borderRadius: 0,
            color: colors.color,
            backgroundColor: colors.backgroundColor,
            fontFamily: (theme) => theme.typography.fontFamilyMonospace,
            fontSize: '12px',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
            },
            '&::before': {
              insetBlock: offset,
              insetInline: 0,
              borderInline: '1px dashed',
              borderColor: colors.dashedBorderColor,
            },
            '&::after': {
              insetBlock: 0,
              insetInline: offset,
              borderBlock: '1px solid',
              borderColor: colors.borderColor,
            },
          },
          sx
        )}
      />
    );
  }
);

export default CalloutChip;
